import React, { useEffect, useState } from 'react';
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    TextField,
} from '@mui/material';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import { panelTheme } from '../../PanelComerciales.theme';
import { Commercial } from '../../PanelComerciales.types';
import { QueueItem } from 'src/redux/panel_comerciales/panel_comerciales.types';

interface QueueAssignDialogProps {
    item: QueueItem | null;
    commercials: Commercial[];
    busy: boolean;
    onClose: () => void;
    onConfirm: (item: QueueItem, sellerId: number) => void | Promise<void>;
}

const NO_SELLER = 0;

/**
 * Asignación rápida desde la cola: la líder confirma (o cambia) el comercial
 * al que se le crea el deal del cliente PENDING. NO es un VIP: el motor
 * re-valida elegibilidad y la elección persiste como relación de cartera.
 */
const QueueAssignDialog: React.FC<QueueAssignDialogProps> = ({
    item,
    commercials,
    busy,
    onClose,
    onConfirm,
}) => {
    const [sellerId, setSellerId] = useState<number>(NO_SELLER);

    useEffect(() => {
        setSellerId(item?.suggestedSeller?.sellerId ?? NO_SELLER);
    }, [item]);

    const activeCommercials = commercials.filter((c) => c.active);
    const suggested = item?.suggestedSeller;

    return (
        <Dialog open={Boolean(item)} onClose={onClose} maxWidth='xs' fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonAddAltOutlinedIcon sx={{ color: panelTheme.colors.indigo }} />
                Asignar desde la cola
            </DialogTitle>
            <DialogContent>
                <Alert severity='info' sx={{ mb: 2 }}>
                    Se crea el deal de {item?.clientName ?? 'este cliente'} hacia el
                    comercial elegido y la fila sale de la cola. No es una cuenta
                    VIP: la elegibilidad se valida de nuevo.
                </Alert>
                <TextField
                    select
                    fullWidth
                    size='small'
                    label='Comercial receptor'
                    value={sellerId}
                    onChange={(e) => setSellerId(Number(e.target.value))}
                    helperText={
                        suggested
                            ? `Sugerido por el motor: ${suggested.name} (regla ${suggested.rule})`
                            : 'El motor no encontró candidato con cupo; elige uno manualmente.'
                    }
                >
                    <MenuItem value={NO_SELLER} disabled>
                        Selecciona un comercial
                    </MenuItem>
                    {activeCommercials.map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                            {c.name} · {c.team}
                        </MenuItem>
                    ))}
                </TextField>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} sx={{ textTransform: 'none' }}>
                    Cancelar
                </Button>
                <Button
                    variant='contained'
                    disabled={sellerId === NO_SELLER || busy || !item}
                    onClick={() => item && onConfirm(item, sellerId)}
                    sx={{
                        textTransform: 'none',
                        background: panelTheme.colors.indigo,
                        '&:hover': { background: panelTheme.colors.violet },
                    }}
                >
                    Asignar
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default QueueAssignDialog;
